<!-- HeaderSettingPopup.vue -->
<template>
  <!-- 헤더 컨텍스트 탈출 -->
  <teleport to="body">
    <div
      ref="modalEl"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settingsLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <!-- Header -->
          <div class="modal-header">
            <slot name="header">
              <span id="settingsLabel" class="modal-title h5">
                {{ $t("shipping.title") }}
              </span>
              <button
                type="button"
                class="btn-close"
                aria-label="Close"
                @click="close"
              ></button>
            </slot>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <slot name="body">
              <form class="row g-3">
                <!-- 국가 설정 -->
                <div class="col-12">
                  <label for="country-select" class="form-label">
                    {{ $t("shipping.country") }}
                  </label>
                  <select
                    id="country-select"
                    class="form-select"
                    v-model="selectedCountry"
                    @change="onCountryChange"
                  >
                    <option
                      v-for="opt in countryOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>

                <!-- 화폐 설정 -->
                <div class="col-12">
                  <label for="currency-select" class="form-label">
                    {{ $t("shipping.currency") }}
                  </label>
                  <select
                    id="currency-select"
                    class="form-select"
                    v-model="selectedCurrency"
                  >
                    <option
                      v-for="opt in currencyOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
              </form>
            </slot>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <slot name="footer">
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="emitClose"
              >
                {{ $t("shipping.cancel") }}
              </button>
              <button
                type="button"
                class="btn btn-primary"
                @click="applySettings"
              >
                {{ $t("shipping.confirm") }}
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
/**
 * Bootstrap 5.3 Modal (ESM)
 * - JS는 ESM 부분 임포트를 권장: `import Modal from "bootstrap/js/dist/modal"`
 * - 전역 bootstrap.bundle을 동시에 로드하지 마세요(이중 바인딩 위험)
 */
import {
  ref,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
  defineProps,
  defineEmits,
  defineOptions,
  defineExpose,
  watch,
  nextTick,
} from "vue";
import Modal from "bootstrap/js/dist/modal";
import { logDebug, logWarn, logError } from "@/utils/logHelper";

defineOptions({ name: "HeaderSettingPopup" });

/* ---------- Props ---------- */
const props = defineProps({
  country: {
    type: String,
    default: "US",
    validator: (v) => ["KR", "US", "JP"].includes(v),
  },
  currency: {
    type: String,
    default: "USD",
    validator: (v) =>
      ["KRW", "USD", "EUR", "GBP", "JPY", "CAD", "AUD"].includes(v),
  },
  language: { type: String, default: "en" },
  locale: { type: String, default: "en-US" },
});

const emit = defineEmits(["update-settings", "close"]);
const { proxy } = getCurrentInstance();

/* ---------- Local state ---------- */
const selectedCountry = ref(props.country);
const selectedCurrency = ref(props.currency);
const selectedLanguage = ref(props.language);
const selectedLocale = ref(props.locale);

/* ---------- Options ---------- */
const countryOptions = [
  { value: "KR", label: "ROK(Republic of Korea)" },
  { value: "US", label: "USA(United States)" },
  { value: "JP", label: "JPN(Japan)" },
];

const currencyOptions = [
  { value: "KRW", label: "KRW(₩)" },
  { value: "USD", label: "USD($)" },
  { value: "EUR", label: "EUR(€)" },
  { value: "GBP", label: "GBP(£)" },
  { value: "JPY", label: "JPY(¥)" },
  { value: "CAD", label: "CAD(C$)" },
  { value: "AUD", label: "AUD(A$)" },
];

/* ---------- Bootstrap Modal ---------- */
const modalEl = ref(null);
let modalInst = null;

onMounted(async () => {
  modalInst = Modal.getOrCreateInstance(modalEl.value, {
    backdrop: true, // 필요 시 'static'
    keyboard: true,
    focus: true,
  });

  modalEl.value.addEventListener("hidden.bs.modal", onHidden);

  // 부모에서 v-if로 마운트되면 즉시 표시
  await nextTick();
  modalInst.show();

  // 초기 국가 → 언어/로케일 매핑
  onCountryChange();
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener("hidden.bs.modal", onHidden);
  modalInst?.dispose();
});

function onHidden() {
  emit("close");
}

/* ---------- API (부모에서 ref로 제어 가능) ---------- */
defineExpose({ open, close });
function open() {
  modalInst?.show();
}
function close() {
  modalInst?.hide();
}

/* ---------- Sync: 부모 -> 내부 선택값 ---------- */
watch(
  () => [props.country, props.currency, props.language, props.locale],
  ([c, cur, lang, loc]) => {
    selectedCountry.value = c;
    selectedCurrency.value = cur;
    selectedLanguage.value = lang;
    selectedLocale.value = loc;
  },
  { immediate: true },
);

/* ---------- Handlers ---------- */
function emitClose() {
  logDebug("[HeaderSettingPopup] emitClose");
  close();
}

function onCountryChange() {
  try {
    if (selectedCountry.value === "KR") {
      selectedLanguage.value = "ko";
      selectedLocale.value = "ko-KR";
    } else if (selectedCountry.value === "US") {
      selectedLanguage.value = "en";
      selectedLocale.value = "en-US";
    } else if (selectedCountry.value === "JP") {
      selectedLanguage.value = "ja";
      selectedLocale.value = "ja-JP";
    } else {
      logWarn("[HeaderSettingPopup] unmapped country:", selectedCountry.value);
      proxy?.$toast?.error("매핑되지 않은 국가입니다.");
    }
  } catch (e) {
    proxy?.$toast?.error("국가/로케일 설정 중 오류가 발생했습니다.");
    logError("[HeaderSettingPopup] onCountryChange Error:", e);
  }
}

function applySettings() {
  logDebug("[HeaderSettingPopup] applySettings");
  emit("update-settings", {
    country: selectedCountry.value,
    language: selectedLanguage.value,
    currency: selectedCurrency.value,
    locale: selectedLocale.value,
  });
  close(); // hidden.bs.modal에서 부모로 close emit
}
</script>

<style scoped>
/* 필요 시 커스텀 스타일 */
</style>
